// Firebase imports
import { 
  collection, 
  addDoc, 
  getDocs, 
  doc, 
  getDoc,
  updateDoc, 
  query, 
  where, 
  orderBy, 
  limit, 
  serverTimestamp,
  Timestamp 
} from 'firebase/firestore';
import { certificationApi } from '@/lib/certification-api';
import { auth, db, deleteCourseContentCallable } from '@/lib/firebase';

import { ICourseRepository } from './interfaces';
import { AnalyticsRepository, AccountContext } from './analytics.repository';
import { DRXError, toDRXError } from '@/lib/drx-error';
import { profileDocRef } from "@/lib/public-profile";
import { normalizeMediaFields } from '@shared/video-links';

// Course entity interfaces
export interface CreateCourseData {
  title: string;
  description: string;
  price: number;
  imageUrl?: string;
  instructorId: string;
  previewVideoUrl: string;
  videoUrl: string;
  detailedDescription: string;
  difficultyLevel: string;
  duration: string;
  shortName?: string;
  visible: boolean;
  averageRating?: number;
  isPublished?: boolean;
  categoryIds?: string[];
  tagIds?: string[];
}

export interface UpdateCourseData {
  title?: string;
  description?: string;
  price?: number;
  imageUrl?: string;
  previewVideoUrl?: string;
  videoUrl?: string;
  detailedDescription?: string;
  difficultyLevel?: string;
  duration?: string;
  shortName?: string;
  visible?: boolean;
  averageRating?: number;
  isPublished?: boolean;
  categoryIds?: string[];
  tagIds?: string[];
  updatedAt?: Timestamp;
}

export interface CourseFilter {
  instructorId?: string;
  difficultyLevel?: string;
  visible?: boolean;
  isPublished?: boolean;
  categoryId?: string;
  tagId?: string;
  priceMin?: number;
  priceMax?: number;
}

export interface GetCourseData {
  id: string;
  title: string;
  description: string;
  price: number;
  imageUrl?: string;
  instructorId: string;
  previewVideoUrl: string;
  videoUrl: string;
  detailedDescription: string;
  difficultyLevel: string;
  duration: string;
  shortName?: string;
  visible: boolean;
  averageRating?: number;
  isPublished: boolean;
  categoryIds?: string[];
  tagIds?: string[];
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

export interface EnrollUserData {
  userId: string;
  courseId: string;
  enrollmentStatus?: string;
}

export interface GetEnrollmentData {
  id: string;
  userId: string;
  courseId: string;
  enrollmentStatus: string;
  enrolledAt: Timestamp;
  completedAt?: Timestamp;
  progressPercentage: number;
}

/**
 * The authoring form carries `status: draft | published | archived`, but the
 * catalog and the course page filter on the boolean `visible`. Nothing mapped
 * one onto the other, so a course marked Published in the UI stayed invisible.
 * Derive the boolean whenever a status is supplied, without inventing one when
 * it is not (a partial update must not silently unpublish a course).
 */
function visibilityFromStatus(data: Record<string, unknown>): { visible?: boolean; isPublished?: boolean } {
  const status = typeof data.status === 'string' ? data.status.toLowerCase() : undefined;
  if (!status) return {};
  const published = status === 'published';
  return { visible: published, isPublished: published };
}

export class CourseRepository implements ICourseRepository {
  private collectionName = 'certificationCourses';
  private enrollmentCollectionName = 'enrollments';

  constructor(private analyticsRepo?: AnalyticsRepository) {
    console.log('[CourseRepository] Constructor - Collection:', this.collectionName, 'DB instance:', !!db);
  }

  private toRepositoryError(error: unknown, code: string, devMessage: string) {
    return toDRXError(error, {
      code,
      devMessage,
    });
  }

  // Create course
  async createCourse(courseData: CreateCourseData, context?: AccountContext): Promise<GetCourseData> {
    console.log('[CourseRepository.createCourse] START - CourseData:', courseData, 'Context:', context);
    try {
      console.log('[CourseRepository.createCourse] Creating course document with analytics tracking');
      // Firestore rejects an explicit `undefined`, so an absent account context
      // has to omit the key entirely rather than write it as undefined. This
      // threw "Unsupported field value: undefined (found in field accountId)"
      // and made course creation fail outright whenever no account was passed.
      const docRef = await addDoc(collection(db, this.collectionName), {
        ...normalizeMediaFields(courseData as unknown as Record<string, unknown>),
        ...visibilityFromStatus(courseData as unknown as Record<string, unknown>),
        ...(context?.accountId ? { accountId: context.accountId } : {}),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      const docSnap = await getDoc(docRef);
      if (!docSnap.exists()) {
        throw new DRXError({
          code: 'COURSE_CREATE_MISSING_DOCUMENT',
          devMessage: 'Course document was missing after create',
        });
      }

      const courseDataResult = {
        id: docRef.id,
        ...docSnap.data(),
      } as GetCourseData;

      // Telemetry must never fail the write it is describing. The course
      // document is already committed by this point; if analytics throws, the
      // outer catch would turn a successful creation into "Failed to create
      // course" and the instructor would retry, duplicating the course.
      try {
      if (this.analyticsRepo) {
        await this.analyticsRepo.trackAnalyticsEvent(
          'courseCreated',
          {
            courseId: courseDataResult.id,
            title: courseDataResult.title,
            instructorId: courseDataResult.instructorId,
            price: courseDataResult.price,
            // Same undefined-rejection rule applies to the analytics payload.
            ...(context?.accountId ? { accountId: context.accountId } : {}),
          },
          courseData.instructorId,
          context
        );

        await this.analyticsRepo.trackEntityActivity(
          'course',
          courseDataResult.id,
          'create',
          courseData.instructorId,
          {
            title: courseDataResult.title,
            price: courseDataResult.price,
            category: 'course',
          },
          context
        );
      }
      } catch (analyticsError) {
        console.warn('[CourseRepository.createCourse] analytics failed (course still created):', analyticsError);
      }

      console.log('[CourseRepository.createCourse] SUCCESS - Created course with ID:', courseDataResult.id);
      return courseDataResult;
    } catch (error) {
      console.error('[CourseRepository.createCourse] ERROR:', error);
      throw this.toRepositoryError(error, 'COURSE_CREATE_FAILED', 'Failed to create course');
    }
  }

  // Get course by ID
  async getCourseById(courseId: string): Promise<GetCourseData | null> {
    console.log('[CourseRepository.getCourseById] START - CourseId:', courseId);
    if (!courseId || courseId.length > 1500) {
      console.log('[CourseRepository.getCourseById] Invalid courseId (empty or >1500 bytes)');
      return null;
    }
    try {
      console.log('[CourseRepository.getCourseById] Fetching course document from Firestore');
      const docRef = doc(db, this.collectionName, courseId);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        console.log('[CourseRepository.getCourseById] Course not found');
        return null;
      }

      const courseData = {
        id: docSnap.id,
        ...docSnap.data(),
      } as GetCourseData;
      console.log('[CourseRepository.getCourseById] SUCCESS - Found course:', courseData.title);
      return courseData;
    } catch (error) {
      console.error('[CourseRepository.getCourseById] ERROR:', error);
      throw this.toRepositoryError(error, 'COURSE_FETCH_FAILED', `Failed to get course ${courseId}`);
    }
  }

  // Get courses with filters
  async getCourses(filter: CourseFilter = {}, limitCount?: number): Promise<GetCourseData[]> {
    console.log('[CourseRepository.getCourses] START - Filter:', filter, 'LimitCount:', limitCount);
    try {
      console.log('[CourseRepository.getCourses] Building Firestore query with filters');
      console.log('[CourseRepository.getCourses] Collection name:', this.collectionName);

      // First, let's try a simple query to see if we can access the collection at all
      console.log('[CourseRepository.getCourses] Testing basic collection access...');
      try {
        const testCollection = collection(db, this.collectionName);
        console.log('[CourseRepository.getCourses] Collection reference created successfully');

        // Try to get all documents in the collection without any filters
        const allDocsQuery = query(testCollection, limit(100));
        const allDocsSnapshot = await getDocs(allDocsQuery);
        console.log('[CourseRepository.getCourses] UNFILTERED query result:', allDocsSnapshot.size, 'documents in courses collection');

        // Log some document IDs if they exist
        if (allDocsSnapshot.size > 0) {
          console.log('[CourseRepository.getCourses] Found document IDs:', allDocsSnapshot.docs.slice(0, 5).map(doc => doc.id));
        } else {
          // Try alternative collection names
          console.log('[CourseRepository.getCourses] No courses found in "courses" collection. Trying alternative names...');

          const alternativeCollections = ['Courses', 'course', 'Course', 'coursesCollection'];
          for (const altCollection of alternativeCollections) {
            try {
              console.log(`[CourseRepository.getCourses] Trying collection: ${altCollection}`);
              const altQuery = query(collection(db, altCollection), limit(10));
              const altSnapshot = await getDocs(altQuery);
              console.log(`[CourseRepository.getCourses] ${altCollection} collection has:`, altSnapshot.size, 'documents');
              if (altSnapshot.size > 0) {
                console.log(`[CourseRepository.getCourses] Found in ${altCollection}:`, altSnapshot.docs.slice(0, 3).map(doc => doc.id));
              }
            } catch (altError) {
              console.log(`[CourseRepository.getCourses] Error accessing ${altCollection}:`, altError.message);
            }
          }
        }
      } catch (testError) {
        console.error('[CourseRepository.getCourses] Collection access test failed:', testError);
      }

      let q = collection(db, this.collectionName);

      // Build query constraints array
      const constraints = [];

      // Add filters
      if (filter.instructorId) {
        constraints.push(where('instructorId', '==', filter.instructorId));
      }
      if (filter.difficultyLevel) {
        constraints.push(where('difficultyLevel', '==', filter.difficultyLevel));
      }
      if (filter.visible !== undefined) {
        constraints.push(where('visible', '==', filter.visible));
      }
      if (filter.isPublished !== undefined) {
        constraints.push(where('isPublished', '==', filter.isPublished));
      }
      if (filter.categoryId) {
        constraints.push(where('categoryIds', 'array-contains', filter.categoryId));
      }
      if (filter.tagId) {
        constraints.push(where('tagIds', 'array-contains', filter.tagId));
      }

      // Add limit if specified
      if (limitCount) {
        constraints.push(limit(limitCount));
      }

      // Build the final query
      if (constraints.length > 0) {
        // We have filters or limit, build query with constraints
        q = query(q, ...constraints);
        console.log('[CourseRepository.getCourses] Built query with', constraints.length, 'constraints');
      } else {
        // No filters, no limit - just get all documents from collection
        console.log('[CourseRepository.getCourses] No constraints, using simple collection query');
        // q is already set to collection(db, this.collectionName)
      }

      console.log('[CourseRepository.getCourses] Executing query...');
      console.log('[CourseRepository.getCourses] Query details:', {
        collection: this.collectionName,
        filters: Object.keys(filter).length,
        limit: limitCount
      });

      const querySnapshot = await getDocs(q);
      console.log('[CourseRepository.getCourses] Query executed, docs count:', querySnapshot.size);

      // If no documents found, let's try a simpler query to see if the collection exists
      if (querySnapshot.size === 0) {
        console.log('[CourseRepository.getCourses] No documents found. Testing collection access...');
        try {
          const simpleQuery = collection(db, this.collectionName);
          const simpleSnapshot = await getDocs(simpleQuery);
          console.log('[CourseRepository.getCourses] Simple query result:', simpleSnapshot.size, 'documents');
        } catch (simpleError) {
          console.error('[CourseRepository.getCourses] Simple query failed:', simpleError);
        }
      }
      const courses: GetCourseData[] = [];

      querySnapshot.forEach((doc) => {
        const docData = doc.data();
        console.log('[CourseRepository.getCourses] Processing doc:', doc.id, 'with data keys:', Object.keys(docData));
        courses.push({
          id: doc.id,
          ...docData,
        } as GetCourseData);
      });

      // Apply price filters (since Firestore doesn't support range queries with other filters easily)
      let filteredCourses = courses;
      // Public listings leave out a suspended instructor's courses.
      if (filter.visible === true || filter.isPublished === true) {
        filteredCourses = filteredCourses.filter(
          (course) => (course as { ownerSuspended?: boolean }).ownerSuspended !== true
        );
      }
      if (filter.priceMin !== undefined) {
        filteredCourses = filteredCourses.filter(course => course.price >= filter.priceMin!);
      }
      if (filter.priceMax !== undefined) {
        filteredCourses = filteredCourses.filter(course => course.price <= filter.priceMax!);
      }

      // Sort by creation date (manual sorting as fallback when Firebase ordering fails)
      filteredCourses.sort((a, b) => {
        const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return dateB - dateA; // Descending order
      });

      console.log('[CourseRepository.getCourses] SUCCESS - Retrieved', filteredCourses.length, 'courses');
      return filteredCourses;
    } catch (error) {
      console.error('[CourseRepository.getCourses] ERROR:', error);
      throw this.toRepositoryError(error, 'COURSES_FETCH_FAILED', 'Failed to get courses');
    }
  }

  // Update course
  async updateCourse(courseId: string, updateData: UpdateCourseData): Promise<GetCourseData> {
    console.log('[CourseRepository.updateCourse] START - CourseId:', courseId, 'UpdateData:', updateData);
    try {
      console.log('[CourseRepository.updateCourse] Updating course document in Firestore');
      const docRef = doc(db, this.collectionName, courseId);
      await updateDoc(docRef, {
        // No YouTube Studio links stored: they become the public link (shared/video-links.ts).
        ...normalizeMediaFields(updateData as unknown as Record<string, unknown>),
        ...visibilityFromStatus(updateData as unknown as Record<string, unknown>),
        updatedAt: serverTimestamp(),
      });

      const docSnap = await getDoc(docRef);
      if (!docSnap.exists()) {
        throw new DRXError({
          code: 'COURSE_NOT_FOUND_AFTER_UPDATE',
          devMessage: `Course ${courseId} not found after update`,
          userMessage: 'Course not found.',
          level: 'warning',
        });
      }

      const updatedCourse = {
        id: docSnap.id,
        ...docSnap.data(),
      } as GetCourseData;
      console.log('[CourseRepository.updateCourse] SUCCESS - Updated course:', updatedCourse.title);
      return updatedCourse;
    } catch (error) {
      console.error('[CourseRepository.updateCourse] ERROR:', error);
      throw this.toRepositoryError(error, 'COURSE_UPDATE_FAILED', `Failed to update course ${courseId}`);
    }
  }

  // Delete course
  async deleteCourse(courseId: string): Promise<void> {
    console.log('[CourseRepository.deleteCourse] START - CourseId:', courseId);
    try {
      // Deleting only this document left everything under it -- modules,
      // lessons, quizzes, questions, material records -- and every uploaded
      // file behind. The server deletes the whole tree and its files, and
      // checks the caller owns the course (the rules only let admins delete).
      await deleteCourseContentCallable({ scope: 'course', id: courseId });
      console.log('[CourseRepository.deleteCourse] SUCCESS - Deleted course');
    } catch (error) {
      console.error('[CourseRepository.deleteCourse] ERROR:', error);
      throw this.toRepositoryError(error, 'COURSE_DELETE_FAILED', `Failed to delete course ${courseId}`);
    }
  }

  // Get courses by instructor
  async getCoursesByInstructor(instructorId: string, limitCount?: number): Promise<GetCourseData[]> {
    console.log('[CourseRepository.getCoursesByInstructor] START - InstructorId:', instructorId, 'LimitCount:', limitCount);
    try {
      console.log('[CourseRepository.getCoursesByInstructor] Calling getCourses with instructor filter');
      const filter: CourseFilter = { instructorId: instructorId };
      const result = await this.getCourses(filter, limitCount);
      console.log('[CourseRepository.getCoursesByInstructor] SUCCESS - Retrieved', result.length, 'courses for instructor');
      return result;
    } catch (error) {
      console.error('[CourseRepository.getCoursesByInstructor] ERROR:', error);
      throw this.toRepositoryError(error, 'COURSES_BY_INSTRUCTOR_FETCH_FAILED', `Failed to get courses for instructor ${instructorId}`);
    }
  }

  async getCoursesByModuleId(moduleId: string, limitCount?: number): Promise<GetCourseData[]> {
    console.log('[CourseRepository.getCoursesByModuleId] START - ModuleId:', moduleId, 'LimitCount:', limitCount);
    try {
      const constraints = [
        where('moduleId', '==', moduleId),
        orderBy('moduleSequence', 'asc'),
      ];
      if (limitCount) {
        constraints.push(limit(limitCount));
      }
      const q = query(collection(db, this.collectionName), ...constraints);
      const querySnapshot = await getDocs(q);
      const courses: GetCourseData[] = [];
      querySnapshot.forEach((docSnap) => {
        courses.push({
          id: docSnap.id,
          ...docSnap.data(),
        } as GetCourseData);
      });
      console.log('[CourseRepository.getCoursesByModuleId] SUCCESS - Retrieved', courses.length, 'courses');
      return courses;
    } catch (error) {
      console.error('[CourseRepository.getCoursesByModuleId] ERROR:', error);
      throw this.toRepositoryError(
        error,
        'COURSES_BY_MODULE_FETCH_FAILED',
        `Failed to get courses for module ${moduleId}`,
      );
    }
  }

  // Get published courses
  async getPublishedCourses(limitCount?: number): Promise<GetCourseData[]> {
    console.log('[CourseRepository.getPublishedCourses] START - LimitCount:', limitCount);
    try {
      console.log('[CourseRepository.getPublishedCourses] Getting published and visible courses');
      const filter: CourseFilter = { isPublished: true, visible: true };
      const result = await this.getCourses(filter, limitCount);
      console.log('[CourseRepository.getPublishedCourses] SUCCESS - Retrieved', result.length, 'published courses');
      return result;
    } catch (error) {
      console.error('[CourseRepository.getPublishedCourses] ERROR:', error);
      throw this.toRepositoryError(error, 'COURSES_PUBLISHED_FETCH_FAILED', 'Failed to get published courses');
    }
  }

  // Enroll user in course
  async enrollUser(enrollmentData: EnrollUserData): Promise<GetEnrollmentData> {
    console.log('[CourseRepository.enrollUser] START - EnrollmentData:', enrollmentData);
    try {
      // Enrollments are server-written (firestore.rules): yourself into a free
      // course, or -- as course staff -- a student into your course.
      const self = !auth.currentUser?.uid || auth.currentUser.uid === enrollmentData.userId;
      const { enrollmentId } = self
        ? await certificationApi.enrollInFreeCourse(String(enrollmentData.courseId))
        : await certificationApi.instructorEnrollStudent(String(enrollmentData.courseId), String(enrollmentData.userId));
      const docRef = doc(db, this.enrollmentCollectionName, enrollmentId);

      const docSnap = await getDoc(docRef);
      if (!docSnap.exists()) {
        throw new DRXError({
          code: 'COURSE_ENROLLMENT_CREATE_MISSING_DOCUMENT',
          devMessage: `Enrollment document missing after create for user ${enrollmentData.userId} and course ${enrollmentData.courseId}`,
        });
      }

      const enrollment = {
        id: docRef.id,
        ...docSnap.data(),
      } as GetEnrollmentData;
      console.log('[CourseRepository.enrollUser] SUCCESS - Created enrollment with ID:', enrollment.id);
      return enrollment;
    } catch (error) {
      console.error('[CourseRepository.enrollUser] ERROR:', error);
      throw this.toRepositoryError(error, 'COURSE_ENROLLMENT_CREATE_FAILED', `Failed to enroll user ${enrollmentData.userId} in course ${enrollmentData.courseId}`);
    }
  }

  // Get course by title
  async getCourseByTitle(title: string): Promise<GetCourseData | null> {
    console.log('[CourseRepository.getCourseByTitle] START - Title:', title);
    try {
      console.log('[CourseRepository.getCourseByTitle] Querying course by title');
      const q = query(collection(db, this.collectionName), where('title', '==', title));
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        console.log('[CourseRepository.getCourseByTitle] Course not found with title:', title);
        return null;
      }

      const doc = querySnapshot.docs[0];
      const course = {
        id: doc.id,
        ...doc.data(),
      } as GetCourseData;
      console.log('[CourseRepository.getCourseByTitle] SUCCESS - Found course with ID:', course.id);
      return course;
    } catch (error) {
      console.error('[CourseRepository.getCourseByTitle] ERROR:', error);
      throw this.toRepositoryError(error, 'COURSE_BY_TITLE_FETCH_FAILED', `Failed to get course by title ${title}`);
    }
  }

  // Get course by short name
  async getCourseByShortName(shortName: string): Promise<GetCourseData | null> {
    console.log('[CourseRepository.getCourseByShortName] START - ShortName:', shortName);
    try {
      console.log('[CourseRepository.getCourseByShortName] Querying course by short name');
      const q = query(collection(db, this.collectionName), where('shortName', '==', shortName));
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        console.log('[CourseRepository.getCourseByShortName] Course not found with short name:', shortName);
        return null;
      }

      const doc = querySnapshot.docs[0];
      const course = {
        id: doc.id,
        ...doc.data(),
      } as GetCourseData;
      console.log('[CourseRepository.getCourseByShortName] SUCCESS - Found course with ID:', course.id);
      return course;
    } catch (error) {
      console.error('[CourseRepository.getCourseByShortName] ERROR:', error);
      throw this.toRepositoryError(error, 'COURSE_BY_SHORT_NAME_FETCH_FAILED', `Failed to get course by short name ${shortName}`);
    }
  }

  // Get courses by difficulty level
  async getCoursesByDifficultyLevel(difficultyLevel: string, limitCount?: number): Promise<GetCourseData[]> {
    console.log('[CourseRepository.getCoursesByDifficultyLevel] START - DifficultyLevel:', difficultyLevel, 'LimitCount:', limitCount);
    try {
      console.log('[CourseRepository.getCoursesByDifficultyLevel] Filtering courses by difficulty level');
      const filter: CourseFilter = { difficultyLevel: difficultyLevel };
      const result = await this.getCourses(filter, limitCount);
      console.log('[CourseRepository.getCoursesByDifficultyLevel] SUCCESS - Retrieved', result.length, 'courses for difficulty level');
      return result;
    } catch (error) {
      console.error('[CourseRepository.getCoursesByDifficultyLevel] ERROR:', error);
      throw this.toRepositoryError(error, 'COURSES_BY_DIFFICULTY_FETCH_FAILED', `Failed to get courses for difficulty ${difficultyLevel}`);
    }
  }

  // Get courses by category
  async getCoursesByCategory(categoryId: string, limitCount?: number): Promise<GetCourseData[]> {
    console.log('[CourseRepository.getCoursesByCategory] START - CategoryId:', categoryId, 'LimitCount:', limitCount);
    try {
      console.log('[CourseRepository.getCoursesByCategory] Filtering courses by category');
      const filter: CourseFilter = { categoryId: categoryId };
      const result = await this.getCourses(filter, limitCount);
      console.log('[CourseRepository.getCoursesByCategory] SUCCESS - Retrieved', result.length, 'courses for category');
      return result;
    } catch (error) {
      console.error('[CourseRepository.getCoursesByCategory] ERROR:', error);
      throw this.toRepositoryError(error, 'COURSES_BY_CATEGORY_FETCH_FAILED', `Failed to get courses for category ${categoryId}`);
    }
  }

  // Get courses by tag
  async getCoursesByTag(tagId: string, limitCount?: number): Promise<GetCourseData[]> {
    console.log('[CourseRepository.getCoursesByTag] START - TagId:', tagId, 'LimitCount:', limitCount);
    try {
      console.log('[CourseRepository.getCoursesByTag] Filtering courses by tag');
      const filter: CourseFilter = { tagId: tagId };
      const result = await this.getCourses(filter, limitCount);
      console.log('[CourseRepository.getCoursesByTag] SUCCESS - Retrieved', result.length, 'courses for tag');
      return result;
    } catch (error) {
      console.error('[CourseRepository.getCoursesByTag] ERROR:', error);
      throw this.toRepositoryError(error, 'COURSES_BY_TAG_FETCH_FAILED', `Failed to get courses for tag ${tagId}`);
    }
  }

  // Get courses by price range
  async getCoursesByPriceRange(minPrice: number, maxPrice: number, limitCount?: number): Promise<GetCourseData[]> {
    console.log('[CourseRepository.getCoursesByPriceRange] START - MinPrice:', minPrice, 'MaxPrice:', maxPrice, 'LimitCount:', limitCount);
    try {
      console.log('[CourseRepository.getCoursesByPriceRange] Filtering courses by price range');
      const filter: CourseFilter = {
        priceMin: minPrice,
        priceMax: maxPrice
      };
      const result = await this.getCourses(filter, limitCount);
      console.log('[CourseRepository.getCoursesByPriceRange] SUCCESS - Retrieved', result.length, 'courses in price range');
      return result;
    } catch (error) {
      console.error('[CourseRepository.getCoursesByPriceRange] ERROR:', error);
      throw this.toRepositoryError(error, 'COURSES_BY_PRICE_RANGE_FETCH_FAILED', `Failed to get courses in price range ${minPrice}-${maxPrice}`);
    }
  }

  // Get courses by owner (instructor)
  async getCoursesByOwner(ownerId: string, limitCount?: number): Promise<GetCourseData[]> {
    console.log('[CourseRepository.getCoursesByOwner] START - OwnerId:', ownerId, 'LimitCount:', limitCount);
    try {
      console.log('[CourseRepository.getCoursesByOwner] Delegating to getCoursesByInstructor');
      const result = await this.getCoursesByInstructor(ownerId, limitCount);
      console.log('[CourseRepository.getCoursesByOwner] SUCCESS - Retrieved', result.length, 'courses for owner');
      return result;
    } catch (error) {
      console.error('[CourseRepository.getCoursesByOwner] ERROR:', error);
      throw this.toRepositoryError(error, 'COURSES_BY_OWNER_FETCH_FAILED', `Failed to get courses for owner ${ownerId}`);
    }
  }

  // Get all courses (admin only) or owner's courses
  async getCoursesForUser(userId: string, isAdmin: boolean = false, limitCount?: number): Promise<GetCourseData[]> {
    console.log('[CourseRepository.getCoursesForUser] START - UserId:', userId, 'IsAdmin:', isAdmin, 'LimitCount:', limitCount);
    try {
      console.log('[CourseRepository.getCoursesForUser] Determining access level and retrieving courses');
      let result: GetCourseData[];
      if (isAdmin) {
        // Admin sees all courses
        console.log('[CourseRepository.getCoursesForUser] Admin access - retrieving all courses');
        result = await this.getCourses({}, limitCount);
      } else {
        // Regular user sees only their own courses
        console.log('[CourseRepository.getCoursesForUser] Regular user access - retrieving owned courses');
        result = await this.getCoursesByOwner(userId, limitCount);
      }
      console.log('[CourseRepository.getCoursesForUser] SUCCESS - Retrieved', result.length, 'courses for user');
      return result;
    } catch (error) {
      console.error('[CourseRepository.getCoursesForUser] ERROR:', error);
      throw this.toRepositoryError(error, 'COURSES_FOR_USER_FETCH_FAILED', `Failed to get courses for user ${userId}`);
    }
  }

  // Upload course thumbnail image
  async uploadCourseThumbnail(courseId: string, file: File): Promise<string> {
    console.log('[CourseRepository.uploadCourseThumbnail] START - CourseId:', courseId, 'FileName:', file.name, 'FileSize:', file.size);
    try {
      console.log('[CourseRepository.uploadCourseThumbnail] Simulating thumbnail upload process');

      // Simulate upload delay
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Generate a mock URL
      const imageUrl = `https://storage.example.com/courses/${courseId}/thumbnail_${Date.now()}_${file.name}`;

      // Update course with new thumbnail URL
      await this.updateCourse(courseId, { imageUrl: imageUrl });

      console.log('[CourseRepository.uploadCourseThumbnail] SUCCESS - Uploaded thumbnail:', imageUrl);
      return imageUrl;
    } catch (error) {
      console.error('[CourseRepository.uploadCourseThumbnail] ERROR:', error);
      throw this.toRepositoryError(error, 'COURSE_THUMBNAIL_UPLOAD_FAILED', `Failed to upload course thumbnail for ${courseId}`);
    }
  }

  // Upload course preview video
  async uploadCourseVideo(courseId: string, file: File): Promise<string> {
    console.log('[CourseRepository.uploadCourseVideo] START - CourseId:', courseId, 'FileName:', file.name, 'FileSize:', file.size);
    try {
      console.log('[CourseRepository.uploadCourseVideo] Simulating video upload process');

      // Simulate upload delay (videos take longer)
      await new Promise(resolve => setTimeout(resolve, 3000));

      // Generate a mock URL
      const videoUrl = `https://storage.example.com/courses/${courseId}/video_${Date.now()}_${file.name}`;

      // Update course with new video URL
      await this.updateCourse(courseId, { videoUrl: videoUrl });

      console.log('[CourseRepository.uploadCourseVideo] SUCCESS - Uploaded video:', videoUrl);
      return videoUrl;
    } catch (error) {
      console.error('[CourseRepository.uploadCourseVideo] ERROR:', error);
      throw this.toRepositoryError(error, 'COURSE_VIDEO_UPLOAD_FAILED', `Failed to upload course video for ${courseId}`);
    }
  }

  // Upload course preview video (separate from main video)
  async uploadCoursePreviewVideo(courseId: string, file: File): Promise<string> {
    console.log('[CourseRepository.uploadCoursePreviewVideo] START - CourseId:', courseId, 'FileName:', file.name, 'FileSize:', file.size);
    try {
      console.log('[CourseRepository.uploadCoursePreviewVideo] Simulating preview video upload process');

      // Simulate upload delay
      await new Promise(resolve => setTimeout(resolve, 2000));

      // Generate a mock URL
      const previewVideoUrl = `https://storage.example.com/courses/${courseId}/preview_${Date.now()}_${file.name}`;

      // Update course with new preview video URL
      await this.updateCourse(courseId, { previewVideoUrl: previewVideoUrl });

      console.log('[CourseRepository.uploadCoursePreviewVideo] SUCCESS - Uploaded preview video:', previewVideoUrl);
      return previewVideoUrl;
    } catch (error) {
      console.error('[CourseRepository.uploadCoursePreviewVideo] ERROR:', error);
      throw this.toRepositoryError(error, 'COURSE_PREVIEW_VIDEO_UPLOAD_FAILED', `Failed to upload course preview video for ${courseId}`);
    }
  }

  // Delete course thumbnail
  async deleteCourseThumbnail(courseId: string): Promise<void> {
    console.log('[CourseRepository.deleteCourseThumbnail] START - CourseId:', courseId);
    try {
      console.log('[CourseRepository.deleteCourseThumbnail] Checking for existing thumbnail to delete');
      const course = await this.getCourseById(courseId);
      if (!course?.imageUrl) {
        console.log('[CourseRepository.deleteCourseThumbnail] No thumbnail to delete');
        return; // No thumbnail to delete
      }

      // In real implementation, delete from cloud storage
      console.log('[CourseRepository.deleteCourseThumbnail] Deleting course thumbnail from storage:', course.imageUrl);

      // Update course to remove thumbnail URL
      await this.updateCourse(courseId, { imageUrl: null });
      console.log('[CourseRepository.deleteCourseThumbnail] SUCCESS - Deleted course thumbnail');
    } catch (error) {
      console.error('[CourseRepository.deleteCourseThumbnail] ERROR:', error);
      throw this.toRepositoryError(error, 'COURSE_THUMBNAIL_DELETE_FAILED', `Failed to delete course thumbnail for ${courseId}`);
    }
  }

  // Delete course video
  async deleteCourseVideo(courseId: string): Promise<void> {
    console.log('[CourseRepository.deleteCourseVideo] START - CourseId:', courseId);
    try {
      console.log('[CourseRepository.deleteCourseVideo] Checking for existing video to delete');
      const course = await this.getCourseById(courseId);
      if (!course?.videoUrl) {
        console.log('[CourseRepository.deleteCourseVideo] No video to delete');
        return; // No video to delete
      }

      // In real implementation, delete from cloud storage
      console.log('[CourseRepository.deleteCourseVideo] Deleting course video from storage:', course.videoUrl);

      // Update course to remove video URL
      await this.updateCourse(courseId, { videoUrl: null });
      console.log('[CourseRepository.deleteCourseVideo] SUCCESS - Deleted course video');
    } catch (error) {
      console.error('[CourseRepository.deleteCourseVideo] ERROR:', error);
      throw this.toRepositoryError(error, 'COURSE_VIDEO_DELETE_FAILED', `Failed to delete course video for ${courseId}`);
    }
  }

  // Get user enrollments
  async getUserEnrollments(userId: string): Promise<GetEnrollmentData[]> {
    console.log('[CourseRepository.getUserEnrollments] START - UserId:', userId);
    // A signed-out visitor has no enrolments; querying where('userId', '==',
    // undefined) threw and reported an error (Sentry 7780846200).
    if (!userId) return [];
    try {
      console.log('[CourseRepository.getUserEnrollments] Querying user enrollments from Firestore collection:', this.enrollmentCollectionName);
      
      // Now query for specific user
      const q = query(
        collection(db, this.enrollmentCollectionName),
        where('userId', '==', userId)
      );

      const querySnapshot = await getDocs(q);
      console.log('[CourseRepository.getUserEnrollments] User-specific query result - Found', querySnapshot.size, 'enrollments for user:', userId);
      
      const enrollments: GetEnrollmentData[] = [];

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        enrollments.push({
          id: doc.id,
          ...data,
        } as GetEnrollmentData);
      });

      // Sort by enrolledAt in memory (descending order)
      enrollments.sort((a, b) => {
        const dateA = a.enrolledAt ? new Date(a.enrolledAt.seconds * 1000).getTime() : 0;
        const dateB = b.enrolledAt ? new Date(b.enrolledAt.seconds * 1000).getTime() : 0;
        return dateB - dateA;
      });

      console.log('[CourseRepository.getUserEnrollments] SUCCESS - Retrieved', enrollments.length, 'enrollments for user');
      return enrollments;
    } catch (error) {
      console.error('[CourseRepository.getUserEnrollments] ERROR:', error);
      throw this.toRepositoryError(error, 'COURSE_ENROLLMENTS_BY_USER_FETCH_FAILED', `Failed to get enrollments for user ${userId}`);
    }
  }

  // Get course enrollments
  async getCourseEnrollments(courseId: string): Promise<GetEnrollmentData[]> {
    console.log('[CourseRepository.getCourseEnrollments] START - CourseId:', courseId);
    try {
      console.log('[CourseRepository.getCourseEnrollments] Querying course enrollments from Firestore');
      // Remove orderBy to avoid requiring composite index - we can sort in memory if needed
      const q = query(
        collection(db, this.enrollmentCollectionName),
        where('courseId', '==', courseId)
      );

      const querySnapshot = await getDocs(q);
      const enrollments: GetEnrollmentData[] = [];

      querySnapshot.forEach((doc) => {
        enrollments.push({
          id: doc.id,
          ...doc.data(),
        } as GetEnrollmentData);
      });

      // Sort by enrolledAt in memory (descending order)
      enrollments.sort((a, b) => {
        const dateA = a.enrolledAt ? new Date(a.enrolledAt.seconds * 1000).getTime() : 0;
        const dateB = b.enrolledAt ? new Date(b.enrolledAt.seconds * 1000).getTime() : 0;
        return dateB - dateA;
      });

      console.log('[CourseRepository.getCourseEnrollments] SUCCESS - Retrieved', enrollments.length, 'enrollments for course');
      return enrollments;
    } catch (error) {
      console.error('[CourseRepository.getCourseEnrollments] ERROR:', error);
      throw this.toRepositoryError(error, 'COURSE_ENROLLMENTS_BY_COURSE_FETCH_FAILED', `Failed to get enrollments for course ${courseId}`);
    }
  }

  /**
   * Get courses by module ID
   */
  async getCoursesByModule(moduleId: string): Promise<GetCourseData[]> {
    try {
      const q = query(
        collection(db, this.collectionName),
        where('moduleId', '==', moduleId)
      );

      const querySnapshot = await getDocs(q);
      const courses: GetCourseData[] = [];

      querySnapshot.forEach((d) => {
        courses.push({ id: d.id, ...d.data() } as GetCourseData);
      });

      return courses;
    } catch (error) {
      throw new DRXError({
        code: 'COURSE/GET_BY_MODULE_FAILED',
        devMessage: `Failed to get courses by module: ${error}`,
      });
    }
  }

  // Toggle course visibility
  async toggleCourseVisibility(courseId: string): Promise<boolean> {
    try {
      const courseDoc = await getDoc(doc(db, this.collectionName, courseId));
      if (!courseDoc.exists()) {
        throw new DRXError({
          code: 'COURSE/NOT_FOUND',
          devMessage: `Course ${courseId} not found`,
          userMessage: 'Course not found.',
        });
      }

      const currentVisibility = courseDoc.data()?.visible || false;
      await updateDoc(doc(db, this.collectionName, courseId), {
        visible: !currentVisibility,
        updatedAt: serverTimestamp(),
      });

      return !currentVisibility;
    } catch (error) {
      if (error instanceof DRXError) throw error;
      throw new DRXError({
        code: 'COURSE/TOGGLE_VISIBILITY_FAILED',
        devMessage: `Failed to toggle course visibility: ${error}`,
        userMessage: 'Failed to update course visibility.',
      });
    }
  }

  // Get courses with enrollment status for a user
  async getCoursesWithEnrollment(userId?: string, filters?: {
    instructorId?: string;
    categoryId?: string;
    visible?: boolean;
    limit?: number;
    startAfter?: any;
  }): Promise<any[]> {
    try {
      const { instructorId, categoryId, visible, limit: limitCount = 50 } = filters || {};

      const constraints: any[] = [orderBy('createdAt', 'desc')];
      if (instructorId) constraints.push(where('instructorId', '==', instructorId));
      if (categoryId) constraints.push(where('categoryId', '==', categoryId));
      if (visible !== undefined) constraints.push(where('visible', '==', visible));
      constraints.push(limit(limitCount));

      const q = query(collection(db, this.collectionName), ...constraints);
      const snapshot = await getDocs(q);

      const courses = snapshot.docs.map((courseDoc) => ({
        id: courseDoc.id,
        ...courseDoc.data(),
      })) as Array<Record<string, any> & { id: string }>;

      // Batch the enrollment lookups instead of firing one query per course.
      // Previously this was an N+1: a 50-course list = 1 + 50 round-trips. We
      // now fetch all of this user's enrollments for the returned courses in
      // chunks of 30 (Firestore 'in' limit) → at most ceil(N/30) queries.
      if (userId && courses.length) {
        const enrollmentByCourseId = new Map<string, any>();
        const courseIds = courses.map((c) => c.id);
        for (let i = 0; i < courseIds.length; i += 30) {
          const chunk = courseIds.slice(i, i + 30);
          const enrollmentSnapshot = await getDocs(
            query(
              collection(db, this.enrollmentCollectionName),
              where('userId', '==', userId),
              where('courseId', 'in', chunk)
            )
          );
          enrollmentSnapshot.docs.forEach((d) => {
            const data = d.data();
            enrollmentByCourseId.set(data.courseId, data);
          });
        }
        return courses.map((c) => ({
          ...c,
          enrollmentStatus: enrollmentByCourseId.get(c.id) ?? null,
        }));
      }

      return courses.map((c) => ({ ...c, enrollmentStatus: null }));
    } catch (error) {
      throw new DRXError({
        code: 'COURSE/GET_WITH_ENROLLMENT_FAILED',
        devMessage: `Failed to get courses with enrollment: ${error}`,
      });
    }
  }

  // Get a single course with enrollment status and modules/lessons
  async getCourseWithEnrollment(courseId: string, userId?: string): Promise<any> {
    try {
      const courseDoc = await getDoc(doc(db, this.collectionName, courseId));
      if (!courseDoc.exists()) {
        throw new DRXError({
          code: 'COURSE/NOT_FOUND',
          devMessage: `Course ${courseId} not found`,
          userMessage: 'Course not found.',
        });
      }

      const courseData = courseDoc.data();

      let enrollmentStatus = null;
      if (userId) {
        const enrollmentSnapshot = await getDocs(
          query(
            collection(db, this.enrollmentCollectionName),
            where('userId', '==', userId),
            where('courseId', '==', courseId),
            limit(1)
          )
        );
        if (!enrollmentSnapshot.empty) {
          enrollmentStatus = enrollmentSnapshot.docs[0].data();
        }
      }

      const modulesSnapshot = await getDocs(
        query(collection(db, 'modules'), where('courseId', '==', courseId), orderBy('order', 'asc'))
      );

      // Batch all lessons for the course's modules instead of one query per
      // module (was 1 + M round-trips). Fetch in chunks of 30 (Firestore 'in'
      // limit), then group + order client-side (avoids needing a composite
      // moduleId-in + order index).
      const moduleIds = modulesSnapshot.docs.map((m) => m.id);
      const lessonsByModule = new Map<string, any[]>();
      for (let i = 0; i < moduleIds.length; i += 30) {
        const chunk = moduleIds.slice(i, i + 30);
        if (!chunk.length) continue;
        const lessonsSnapshot = await getDocs(
          query(collection(db, 'lessons'), where('moduleId', 'in', chunk))
        );
        lessonsSnapshot.docs.forEach((l) => {
          const data = l.data() as Record<string, any>;
          const arr = lessonsByModule.get(data.moduleId) ?? [];
          arr.push({ id: l.id, ...data });
          lessonsByModule.set(data.moduleId, arr);
        });
      }

      const modules = modulesSnapshot.docs.map((moduleDoc) => {
        const lessons = (lessonsByModule.get(moduleDoc.id) ?? []).sort(
          (a, b) => (a.order ?? 0) - (b.order ?? 0)
        );
        return { id: moduleDoc.id, ...moduleDoc.data(), lessons };
      });

      return { id: courseDoc.id, ...courseData, enrollmentStatus, modules };
    } catch (error) {
      if (error instanceof DRXError) throw error;
      throw new DRXError({
        code: 'COURSE/GET_WITH_ENROLLMENT_FAILED',
        devMessage: `Failed to get course with enrollment: ${error}`,
      });
    }
  }

  // Get enrollments for all courses by an instructor
  async getInstructorEnrollments(instructorId: string): Promise<any[]> {
    try {
      const instructorCourses = await this.getCoursesByInstructor(instructorId);
      const courseIds = instructorCourses.map((c) => c.id);
      if (courseIds.length === 0) return [];

      const enrollmentsPromises = courseIds.map(async (courseId) => {
        const q = query(collection(db, this.enrollmentCollectionName), where('courseId', '==', courseId));
        const snapshot = await getDocs(q);
        return snapshot.docs.map((d) => ({
          id: d.id,
          ...d.data(),
          courseName: instructorCourses.find((c) => c.id === courseId)?.title,
        }));
      });

      const allEnrollments = await Promise.all(enrollmentsPromises);
      return allEnrollments.flat();
    } catch (error) {
      throw new DRXError({
        code: 'COURSE/GET_INSTRUCTOR_ENROLLMENTS_FAILED',
        devMessage: `Failed to get instructor enrollments: ${error}`,
      });
    }
  }

  // Get unique students across all instructor's courses
  async getInstructorStudents(instructorId: string): Promise<any[]> {
    try {
      const instructorCourses = await this.getCoursesByInstructor(instructorId);
      const courseIds = instructorCourses.map((c) => c.id);
      if (courseIds.length === 0) return [];

      const enrollmentsPromises = courseIds.map(async (courseId) => {
        const q = query(collection(db, this.enrollmentCollectionName), where('courseId', '==', courseId));
        const snapshot = await getDocs(q);
        return snapshot.docs.map((d) => d.data().userId);
      });

      const allStudentIds = (await Promise.all(enrollmentsPromises)).flat();
      const uniqueStudentIds = [...new Set(allStudentIds)];

      const studentsPromises = uniqueStudentIds.map(async (userId) => {
        const userDoc = await getDoc(profileDocRef(userId));
        return userDoc.exists() ? { id: userDoc.id, ...userDoc.data() } : null;
      });

      const students = await Promise.all(studentsPromises);
      return students.filter((s) => s !== null);
    } catch (error) {
      throw new DRXError({
        code: 'COURSE/GET_INSTRUCTOR_STUDENTS_FAILED',
        devMessage: `Failed to get instructor students: ${error}`,
      });
    }
  }

  // Get certificates issued by an instructor
  async getInstructorCertificates(instructorId: string): Promise<any[]> {
    try {
      const q = query(collection(db, 'certificates'), where('instructorId', '==', instructorId));
      const snapshot = await getDocs(q);
      return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
    } catch (error) {
      throw new DRXError({
        code: 'COURSE/GET_INSTRUCTOR_CERTIFICATES_FAILED',
        devMessage: `Failed to get instructor certificates: ${error}`,
      });
    }
  }

  // Issue a certificate -- on the server; the browser cannot write `certificates`.
  async issueCertificate(certificateData: any): Promise<any> {
    try {
      const { certificateId, verificationCode } = await certificationApi.issueCertificate(
        String(certificateData.courseId),
        String(certificateData.userId),
        certificateData.templateId || certificateData.certificateTemplateId || null
      );
      return { id: certificateId, ...certificateData, verificationCode, status: 'active' };
    } catch (error) {
      throw new DRXError({
        code: 'COURSE/ISSUE_CERTIFICATE_FAILED',
        devMessage: `Failed to issue certificate: ${error}`,
        userMessage: 'Failed to issue certificate.',
      });
    }
  }

  // Get reviews for a resource
  async getResourceReviews(resourceId: string): Promise<any[]> {
    try {
      const q = query(collection(db, 'reviews'), where('resourceId', '==', resourceId));
      const snapshot = await getDocs(q);
      return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
    } catch (error) {
      throw new DRXError({
        code: 'COURSE/GET_RESOURCE_REVIEWS_FAILED',
        devMessage: `Failed to get resource reviews: ${error}`,
      });
    }
  }
}
